import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { formatDate, type Tab } from "@po/shared";
import { DataTable } from "../components/DataTable";
import { api, type CertificateTreeLevel } from "../lib/api";
import { int } from "../lib/format";

/** Certificate Handling's lists (SPEC §8.13): each a plain list with a note on what it holds. */
const LISTS: Partial<Record<Tab, { table: string; title: string; note: string }>> = {
  certificates: {
    table: "certificates",
    title: "Certificates",
    note: "The certificates of your vessels. Due and Overdue are Sertica's own, as of the last sync. A replaced certificate keeps its number: the old one is Obsolete.",
  },
  "certificate-groups": {
    table: "certificate-groups",
    title: "Certificate groups",
    note: "How Sertica groups the certificates, with each group's rule, the vessels it is kept on and its day settings. Certificates counts your vessels' live (not Obsolete) ones in the group.",
  },
  "certificate-group-rules": {
    table: "certificate-group-rules",
    title: "Certificate group rules",
    note: "Which dates a certificate in a group must, may or cannot have, and the type of each of its six verifications.",
  },
  "certificate-locations": {
    table: "certificate-locations",
    title: "Certificate locations",
    note: "Where each unit keeps its certificates, with how many of your vessels' live certificates are kept there.",
  },
};

export function CertificateListTab({ tab }: { tab: Tab }) {
  const l = LISTS[tab];
  if (!l) return null;
  return (
    <div className="space-y-4">
      <p className="text-[11.5px] text-muted">{l.note} Only the search applies here.</p>
      <DataTable key={tab} table={l.table} title={l.title} lens="plain" />
    </div>
  );
}

const treeQuery = (parent?: string) => ({
  queryKey: ["certificate-tree", parent ?? ""],
  queryFn: () => api<CertificateTreeLevel>(`/certificate-tree${parent ? `?parent=${encodeURIComponent(parent)}` : ""}`),
});

/** Sertica's Certificate Browser: groups open to their subgroups and certificates, a level at a time. */
export function CertificateBrowserTab() {
  const q = useQuery(treeQuery());
  return (
    <section className="border border-line bg-card" aria-label="Certificate browser">
      <div className="toolbar flex items-center gap-3 px-3 h-10">
        <h3 className="flex items-center gap-2 text-[13px] font-medium">
          Certificate browser
          {q.data && <span className="tnum rounded-full bg-white/15 px-2 text-[12px] leading-5">{int(q.data.groups.reduce((n, g) => n + g.certificates, 0))}</span>}
        </h3>
      </div>
      <div className="py-1 text-[12.5px]">
        {q.isError ? (
          <p className="px-3 py-4 text-[var(--danger)]" role="alert">
            Couldn&apos;t load the certificate groups.{" "}
            <button className="underline font-semibold" onClick={() => void q.refetch()}>
              Retry
            </button>
          </p>
        ) : !q.data ? (
          <p className="px-3 py-4 text-muted">Loading…</p>
        ) : !q.data.groups.length ? (
          <p className="px-3 py-4 text-muted">The certificate groups have not been read from Sertica yet.</p>
        ) : (
          <ul role="tree">
            {q.data.groups.map((g) => (
              <GroupNode key={g.id} group={g} depth={0} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

type Group = CertificateTreeLevel["groups"][number];

/** "12 certificates · 3 overdue · 1 due": live ones in the group and its subgroups. */
function counts(g: Group) {
  const parts = [`${int(g.certificates)} ${g.certificates === 1 ? "certificate" : "certificates"}`];
  if (g.overdue) parts.push(`${int(g.overdue)} overdue`);
  if (g.due) parts.push(`${int(g.due)} due`);
  return parts.join(" · ");
}

function GroupNode({ group, depth }: { group: Group; depth: number }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({ ...treeQuery(group.id), enabled: open });
  const pad = { paddingLeft: 12 + depth * 18 };
  const inner = 12 + (depth + 1) * 18 + 20;
  return (
    <li role="treeitem" aria-expanded={open}>
      <button className="w-full flex items-center gap-2 pr-3 py-[5px] text-left hover:bg-hover" style={pad} onClick={() => setOpen((o) => !o)}>
        <span aria-hidden className="w-3 text-muted">
          {open ? "▾" : "▸"}
        </span>
        <span className="font-medium text-ink">{group.name ?? group.no ?? group.id}</span>
        {group.no && group.name && <span className="text-muted">{group.no}</span>}
        <span className="flex-1" />
        <span className={`text-[11.5px] tnum ${group.overdue ? "text-[var(--danger)]" : "text-muted"}`}>
          {group.subgroups > 0 && `${int(group.subgroups)} ${group.subgroups === 1 ? "group" : "groups"} · `}
          {counts(group)}
        </span>
      </button>
      {open && (
        <ul role="group">
          {!q.data ? (
            <li className="py-[5px] text-muted" style={{ paddingLeft: inner }}>
              {q.isError ? "Couldn't load this group." : "Loading…"}
            </li>
          ) : (
            <>
              {q.data.groups.map((g) => (
                <GroupNode key={g.id} group={g} depth={depth + 1} />
              ))}
              {q.data.certificates.map((c) => (
                <li key={c.guid} role="treeitem" className="flex flex-wrap items-center gap-x-2 pr-3 py-[3px] text-ink2" style={{ paddingLeft: inner }}>
                  <span className="tnum text-ink">{c.no}</span>
                  <span className={c.status === "Obsolete" ? "text-muted line-through" : ""}>{c.name}</span>
                  {c.vessel && <span className="text-muted">{c.vessel}</span>}
                  {c.status && c.status !== "Valid" && <span className="rounded-badge border border-line px-1.5 text-[11px]">{c.status}</span>}
                  {c.overdue && <span className="rounded-badge border border-[var(--danger)] text-[var(--danger)] px-1.5 text-[11px]">Overdue</span>}
                  {c.due && <span className="rounded-badge border border-line px-1.5 text-[11px]">Due</span>}
                  <span className="flex-1" />
                  <span className="text-[11.5px] text-muted tnum">
                    {c.expireDate && `Expires ${formatDate(c.expireDate)}`}
                    {c.nextVerificationDate && c.nextVerificationDate !== c.expireDate && ` · Next verification ${formatDate(c.nextVerificationDate)}`}
                  </span>
                </li>
              ))}
              {q.data.certificatesTotal > q.data.certificates.length && (
                <li className="py-[3px] text-[11.5px] text-muted" style={{ paddingLeft: inner }}>
                  And {int(q.data.certificatesTotal - q.data.certificates.length)} more: search for them under Certificate.
                </li>
              )}
              {!q.data.groups.length && !q.data.certificates.length && (
                <li className="py-[3px] text-[11.5px] text-muted" style={{ paddingLeft: inner }}>
                  No certificates on your vessels.
                </li>
              )}
            </>
          )}
        </ul>
      )}
    </li>
  );
}
